import { Typography } from '@recruit-robin/robin-theme/web-components';
import { useContext, useEffect, useMemo, useState } from 'react';

import type { Vacancy } from 'clients/AutoCompleteClient/types';

import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { formatPreviewDescription } from './formatPreviewDescription';
import { useStyles } from './VacancyTooltip.styles';
import { useVacancyPreview } from './useVacancyPreview';

export const VacancyTooltip = ({ vacancy, enabled }: { vacancy: Vacancy; enabled: boolean }) => {
  const { dictionary, locale } = useContext(LocalizationContext);
  const classes = useStyles();
  const identity = JSON.stringify([vacancy.type, vacancy.value, vacancy.db_id, vacancy.job_ats_id]);
  const [requestedIdentity, setRequestedIdentity] = useState<string | null>(null);

  useEffect(() => {
    setRequestedIdentity(null);
    if (!enabled) return;

    const timer = setTimeout(() => setRequestedIdentity(identity), 200);
    return () => clearTimeout(timer);
  }, [enabled, identity]);

  const preview = useVacancyPreview(vacancy, enabled && requestedIdentity === identity);
  const description = useMemo(
    () => formatPreviewDescription(preview.description, locale, preview.isPlainText),
    [preview.description, locale, preview.isPlainText],
  );
  const message = preview.isError
    ? dictionary.vacancyPreviewError
    : preview.isLoading || (enabled && requestedIdentity !== identity)
      ? dictionary.loadingVacancyDetails
      : dictionary.vacancyPreviewUnavailable;

  return (
    <span>
      <Typography variant={(v) => v.body.short} inline>
        {preview.title}
      </Typography>
      {preview.location && (
        <Typography variant={(v) => v.body.short}>
          {dictionary.location}: {preview.location}
        </Typography>
      )}
      <Typography variant={(v) => v.body.short} customCss={classes.description}>
        {preview.description ? <span dangerouslySetInnerHTML={{ __html: description }} /> : message}
      </Typography>
    </span>
  );
};
